import { useTranslation } from 'react-i18next'

import { sendRentalPortalApplicantEmail } from '../../../../../../api'
import { useNotification } from '../../../../../../hooks'
import {
  isDraftLoading,
  isDraftOpenAction,
  useDraftCreationOpener,
} from '../../../../../Draft'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  EmailCreationPopUp,
  EmailDraftCreateActionSelectModal,
  isDirtyEmailCreationForm,
  useEmailCreationFormInitialValues,
  useEmailDraft,
  useEmailGetActiveAccountForCreate,
} from '../../../../../Email'
import { MINIMIZED_MODAL_KEY } from '../../../../../Modal'
import { useApplicantProfileContext } from '../../../RentalPortalApplicantProfile.hooks'
import {
  getApplicantProfileEmailsComposeInitialValues,
  getApplicantProfileEmailsComposeTooltipKey,
  getApplicantProfileEmailsSendErrorKey,
  getHasApplicantProfileEmailsMailbox,
  toApplicantProfileEmailSendData,
  toApplicantProfileEmailsSendError,
} from '../../RentalPortalApplicantProfileEmails.utils'

export function useApplicantProfileEmailsCompose() {
  const { t } = useTranslation()

  const { handleAddSuccessNotification } = useNotification()

  const {
    applicantData,
    applicantId,
    handleCloseModal,
    handleEntityReload,
    setModal,
  } = useApplicantProfileContext()

  const { activeAccountForCreate, loadingAccount } =
    useEmailGetActiveAccountForCreate()

  const emailInitialValues = useEmailCreationFormInitialValues({
    account: activeAccountForCreate,
  })

  const {
    draft: emailDraft,
    draftId: emailDraftId,
    setDraft: setEmailDraft,
  } = useEmailDraft()

  const composeTooltipKey = getApplicantProfileEmailsComposeTooltipKey({
    applicantData,
    hasMailbox: getHasApplicantProfileEmailsMailbox({
      account: activeAccountForCreate,
      isLoading: loadingAccount,
    }),
  })

  const hasComposeBlocker = Boolean(composeTooltipKey)
  const isDraftPending = isDraftLoading(emailDraftId, emailDraft)

  const isComposeDisabled =
    hasComposeBlocker || isDraftPending || loadingAccount

  function handleSuccessSend() {
    handleCloseModal()

    handleAddSuccessNotification(
      t('RentalPortal.ApplicantProfileEmailsSentNotificationKey')
    )

    handleEntityReload()
  }

  async function handleSubmitSend(payload) {
    try {
      return await sendRentalPortalApplicantEmail({
        data: toApplicantProfileEmailSendData(payload),
        id: applicantId,
      })
    } catch (error) {
      throw toApplicantProfileEmailsSendError({
        error,
        message: t(getApplicantProfileEmailsSendErrorKey(error)),
      })
    }
  }

  function getComposeBaseValues() {
    return emailInitialValues
  }

  function openCompose({ action, initialValues = emailInitialValues }) {
    function getIsComposeDraftOpen() {
      return isDraftOpenAction(action)
    }

    setModal(
      <EmailCreationPopUp
        fieldsProps={{ [EMAIL_CREATION_FORM_FIELD_NAME_TO]: { blocked: true } }}
        formFieldsProps={{ hideCcBcc: true }}
        getContainerIsDirty={getIsComposeDraftOpen}
        getIsDirty={isDirtyEmailCreationForm}
        initialValues={getApplicantProfileEmailsComposeInitialValues({
          applicantData,
          emailInitialValues: initialValues,
        })}
        minimizedModalKey={MINIMIZED_MODAL_KEY.RENTAL_PORTAL_APPLICANT_EMAIL}
        minimizedModalSourceId={emailDraftId}
        resizeConfig={{ initialFullScreen: false }}
        onClose={handleCloseModal}
        onSubmit={handleSubmitSend}
        onSuccess={handleSuccessSend}
      />
    )
  }

  function handleCreateNew() {
    setEmailDraft(null, 0)
  }

  const openComposeCreation = useDraftCreationOpener({
    DraftPickerModal: EmailDraftCreateActionSelectModal,
    closeModal: handleCloseModal,
    draft: emailDraft,
    draftId: emailDraftId,
    getInitialValues: getComposeBaseValues,
    minimizeKey: MINIMIZED_MODAL_KEY.RENTAL_PORTAL_APPLICANT_EMAIL,
    openModal: openCompose,
    setModal,
    onCreateNew: handleCreateNew,
  })

  function handleClickCompose() {
    openComposeCreation()
  }

  return {
    composeTooltipKey,
    handleClickCompose,
    isComposeDisabled,
  }
}
